import moment from 'moment';
import { Browser } from '@capacitor/browser';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Build a Google Calendar event creation URL from a training slot.
 *
 * @param {Object} trainingSlot
 * @returns {string}
 */
export function buildGoogleCalendarUrl(trainingSlot) {
  const title = `Private Gym - Training (${trainingSlot.power_rack})`;
  const location = `${trainingSlot.gym.street}, ${trainingSlot.gym.city}`;
  const description = [
    `Training bij ${trainingSlot.gym.name}`,
    trainingSlot.power_rack,
    '',
    'Private Gym - Train wanneer het jou uitkomt',
  ].join('\n');

  const startUtc = moment(trainingSlot.start).utc().format('YYYYMMDDTHHmmss') + 'Z';
  const stopUtc = moment(trainingSlot.stop).utc().format('YYYYMMDDTHHmmss') + 'Z';
  const dates = `${startUtc}/${stopUtc}`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: dates,
    location: location,
    details: description,
    ctz: 'Europe/Amsterdam',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Fold a single ICS line at 75 octets per RFC 5545.
 *
 * @param {string} line
 * @returns {string}
 */
function foldLine(line) {
  if (line.length <= 75) {
    return line;
  }
  const parts = [];
  parts.push(line.substring(0, 75));
  let remaining = line.substring(75);
  while (remaining.length > 0) {
    // Continuation lines start with a space, so max 74 content chars
    parts.push(' ' + remaining.substring(0, 74));
    remaining = remaining.substring(74);
  }
  return parts.join('\r\n');
}

/**
 * Generate a valid ICS (RFC 5545) string for a training slot.
 *
 * @param {Object} trainingSlot
 * @returns {string}
 */
export function generateICS(trainingSlot) {
  const title = `Private Gym - Training (${trainingSlot.power_rack})`;
  const location = `${trainingSlot.gym.street}, ${trainingSlot.gym.city}`;
  const description = [
    `Training bij ${trainingSlot.gym.name}`,
    trainingSlot.power_rack,
    '',
    'Private Gym - Train wanneer het jou uitkomt',
  ].join('\\n');

  const dtStart = moment(trainingSlot.start).format('YYYYMMDDTHHmmss');
  const dtEnd = moment(trainingSlot.stop).format('YYYYMMDDTHHmmss');
  const dtStamp = moment.utc().format('YYYYMMDDTHHmmss') + 'Z';
  const uid = `${trainingSlot.id}@privategym.nl`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Private Gym//Clients App//NL',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;TZID=Europe/Amsterdam:${dtStart}`,
    `DTEND;TZID=Europe/Amsterdam:${dtEnd}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    'ORGANIZER;CN=Private Gym:mailto:info@privategym.nl',
    'IMAGE;VALUE=URI:https://privategym.nl/images/logo.png',
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldLine).join('\r\n') + '\r\n';
}

/**
 * Open Google Calendar in the in-app browser with pre-filled event details.
 *
 * @param {Object} trainingSlot
 * @returns {Promise<void>}
 */
export async function openGoogleCalendar(trainingSlot) {
  const url = buildGoogleCalendarUrl(trainingSlot);
  await Browser.open({ url });
}

/**
 * Generate an ICS file and share it via the native share sheet.
 *
 * @param {Object} trainingSlot
 * @returns {Promise<void>}
 */
export async function shareICS(trainingSlot) {
  const icsString = generateICS(trainingSlot);

  await Filesystem.writeFile({
    path: 'Private-Gym-Training.ics',
    data: icsString,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
  });

  const uri = await Filesystem.getUri({
    path: 'Private-Gym-Training.ics',
    directory: Directory.Cache,
  });

  await Share.share({
    title: 'Private Gym Training',
    text: 'Voeg toe aan je kalender',
    url: uri.uri,
    dialogTitle: 'Voeg toe aan kalender',
  });
}
